import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";

// Compact "set/change password" card that lives inside the family portal.
// Auth is by magic_token in the URL — the family is already trusted here.
export default function FamilyPasswordCard({
  token,
  hasPassword,
  loginEmail,
  onChanged,
}) {
  const [expanded, setExpanded] = useState(false);
  const [email, setEmail] = useState(loginEmail);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/families/token/${token}/password`, {
        login_email: email,
        password,
      });
      toast.success(
        hasPassword
          ? "Password updated. Use it from any device."
          : "Password set. You can now sign in from any device."
      );
      setPassword("");
      setExpanded(false);
      onChanged?.({ has_password: true, login_email: data.login_email });
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) ||
          "Couldn't save. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove the password login for this family?")) return;
    setRemoving(true);
    try {
      await api.delete(`/families/token/${token}/password`);
      toast.success("Password removed. Magic link still works.");
      setEmail("");
      setPassword("");
      onChanged?.({ has_password: false, login_email: "" });
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) || "Couldn't remove password."
      );
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <div className="label-eyebrow text-[#1a2947] mb-1">
            {hasPassword ? "Password sign-in is on" : "Sign in from any device"}
          </div>
          <p className="text-sm text-stone-600">
            {hasPassword ? (
              <>
                Signed in as{" "}
                <span className="font-mono text-stone-800 break-all">{loginEmail}</span>.
                Update or remove any time.
              </>
            ) : (
              <>
                Set an email + password so you can open your family card from
                any device — no invite link needed.
              </>
            )}
          </p>
        </div>
        {!expanded && (
          <Button
            type="button"
            variant="outline"
            className="shrink-0 w-full sm:w-auto rounded-full border-[#1a2947] text-[#1a2947] hover:bg-[#1a2947] hover:text-white"
            onClick={() => setExpanded(true)}
            data-testid="portal-password-toggle-btn"
          >
            <KeyRound className="h-4 w-4 mr-2" />
            {hasPassword ? "Change password" : "Set a password"}
          </Button>
        )}
      </div>

      {expanded && (
        <form
          onSubmit={save}
          className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3"
          data-testid="portal-password-form"
        >
          <div className="space-y-1">
            <Label htmlFor="pw-email">Sign-in email</Label>
            <Input
              id="pw-email"
              type="email"
              inputMode="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="bg-white border-stone-200"
              data-testid="portal-password-email"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pw-pass">
              {hasPassword ? "New password" : "Password"} (8+ chars)
            </Label>
            <Input
              id="pw-pass"
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-white border-stone-200"
              data-testid="portal-password-password"
            />
          </div>
          <div className="md:col-span-2 flex items-center gap-2 flex-wrap">
            <Button
              type="submit"
              disabled={saving}
              className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] text-white"
              data-testid="portal-password-save-btn"
            >
              {saving ? "Saving…" : hasPassword ? "Update" : "Save password"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="rounded-full text-stone-600"
              onClick={() => {
                setExpanded(false);
                setPassword("");
                setEmail(loginEmail);
              }}
              data-testid="portal-password-cancel-btn"
            >
              Cancel
            </Button>
            {hasPassword && (
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-red-600 hover:text-red-700 ml-auto"
                onClick={remove}
                disabled={removing}
                data-testid="portal-password-remove-btn"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {removing ? "Removing…" : "Remove password"}
              </Button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
